import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import React from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useToast } from "@/components";
import { config } from "@/config/config";
import { useI18n } from "@/context/i18n";

import { registerApi } from "./register.api";
import { IRegisterHooks } from "./register.hooks.interface";
import { registerSchema, registerSimpleSchema } from "./register.schema";
import {
  IRegisterFormValues,
  IRegisterRequest,
  UserType,
} from "./register.types";

export const useRegister = (): IRegisterHooks => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useI18n();
  const googleRecaptchaSiteKey = config.googleReCaptchaSiteKey;
  const reCaptchaRef = React.useRef<ReCAPTCHA>(null);
  const [searchParams] = useSearchParams();

  const userType = React.useMemo(() => {
    const userType = searchParams.get("type");
    if (!userType) return "ADVERTISER";
    if (userType === "advertiser") return "ADVERTISER";
    if (userType === "partner") return "PARTNER";
    if (userType === "both") return "BOTH";
    return "ADVERTISER";
  }, [searchParams]);

  const defaultValues = {
    fullName: "",
    email: "",
    acceptTerms: false,
    userType: userType as UserType,
  };

  const isSimple = React.useMemo(() => {
    return searchParams.get("simple") === "true";
  }, [searchParams]);
  const redirectUrl = React.useMemo(() => {
    return searchParams.get("redirectUrl");
  }, [searchParams]);

  const form = useForm<IRegisterFormValues>({
    resolver: zodResolver(isSimple ? registerSimpleSchema : registerSchema),
    defaultValues: isSimple
      ? defaultValues
      : {
          ...defaultValues,
          password: "",
          confirmPassword: "",
        },
  });

  const registerMutation = useMutation({
    mutationFn: (data: IRegisterRequest) => {
      if (isSimple) {
        return registerApi.registerSimple(data);
      }
      return registerApi.register(data);
    },
    onSuccess: () => {
      if (redirectUrl) {
        return (window.location.href = redirectUrl);
      }
      toast({
        title: t("auth.registerSuccess"),
        description: t("auth.verifyEmailToConfirmAccount"),
      });
      navigate({
        pathname: "/login",
        search: location.search,
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message || t("auth.registerFailed"),
        variant: "destructive",
      });
    },
  });

  const handleRegister = async (data: IRegisterFormValues) => {
    try {
      const reCaptchaToken = await reCaptchaRef?.current?.executeAsync();
      if (!reCaptchaToken) {
        toast({
          title: t("generic.error"),
          description: t("auth.reCaptchaFailed"),
        });
        return;
      }
      const requestData: IRegisterRequest = {
        name: data.fullName,
        email: data.email,
        password: data.password,
        userType: data.userType!,
        reCaptchaToken,
      };
      return registerMutation.mutate(requestData);
    } catch (error) {
      console.error(error);
    } finally {
      reCaptchaRef.current?.reset();
    }
  };

  return {
    handleRegister,
    form,
    googleRecaptchaSiteKey,
    reCaptchaRef,
    isSimple,
  };
};
